import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { UserStateEntry, UserStateMap } from "../../types/user-state";
import { putUserStateItem } from "./userStateApiClient";
import { userStateQueryKey } from "./userStateQueryKeys";
import { useUser, useWaitForUserStateRestored } from "../UserProvider";
import { syncPreferencesToCookies } from "../../utils/shared/client-preferences";
import {
  applyUserStateEntry,
  buildOptimisticUserStateEntry,
  resolveUpdateFailureEntry,
  type UpdateUserStateMutationArgs,
} from "./userStateOptimisticQuery";

interface UpdateUserStateMutationContext {
  previousMap: UserStateMap | undefined;
  previousEntry: UserStateEntry | undefined;
}

/**
 * Builds the mutation's `mutationFn`. Split out from the hook so it can be unit tested without
 * rendering (this repo has no renderHook harness — see `userStateOptimisticQuery.ts`'s doc
 * comment for the same reasoning/precedent).
 *
 * - Signed in: unchanged real `PUT /api/user-state/{itemKey}`.
 * - Guest (TEC-404 guest-mode support): there is no server-side user-state to round-trip to.
 *   `onMutate` (below) already wrote the optimistic entry into the `userStateQueryKey` cache —
 *   which `UserProvider.tsx`'s `PersistQueryClientProvider` persists to IndexedDB via
 *   `userStatePersister.ts` — so that write already IS the final write. Echo the same entry
 *   straight back (reading it from the cache `onMutate` just wrote, so it's byte-identical) so
 *   `onSuccess` re-applies it as a no-op instead of skipping the lifecycle entirely.
 */
export function createUpdateUserStateMutationFn(queryClient: QueryClient, isAuthenticated: boolean) {
  return async (args: UpdateUserStateMutationArgs): Promise<UserStateEntry> => {
    if (!isAuthenticated) {
      const current = queryClient.getQueryData<UserStateMap>(userStateQueryKey);
      return current?.[args.itemKey] ?? buildOptimisticUserStateEntry(args.data, args.version);
    }
    return putUserStateItem(args);
  };
}

/**
 * TanStack Query port of `redux/services/userStateApi.ts`'s `useUpdateUserStateItemMutation` +
 * the optimistic/409-conflict handling in `redux/services/userStateOptimistic.ts`'s
 * `handleUserStateUpdateLifecycle`. Same three-phase lifecycle, expressed via TanStack's mutation
 * callbacks instead of RTK Query's `onQueryStarted` + manual cache patches:
 *
 * - `onMutate`: optimistically writes the incremented-version entry into the `userStateQueryKey`
 *   cache immediately (before the request resolves), after cancelling any in-flight refetch of
 *   that query so it can't clobber the optimistic write. Returns the pre-mutation snapshot as
 *   context for rollback.
 * - `onError`: rolls back using that snapshot — unless the failure is a 409 conflict, in which
 *   case (matching the original's behavior) the server's winning entry is written in instead of
 *   reverting to the stale local value. Guest writes never hit this path (no network call to
 *   fail).
 * - `onSuccess`: replaces the optimistic entry with the server's authoritative response (version,
 *   updatedAt may differ from the optimistic guess) when signed in, or re-applies the identical
 *   optimistic entry as a no-op when a guest.
 */
export function useUpdateUserStateItem() {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useUser();
  const waitForUserStateRestored = useWaitForUserStateRestored();

  return useMutation<UserStateEntry, unknown, UpdateUserStateMutationArgs, UpdateUserStateMutationContext>({
    mutationFn: createUpdateUserStateMutationFn(queryClient, isAuthenticated),
    async onMutate({ itemKey, data, version }) {
      // Must happen before any cache read/write below — see UserContextValue.waitForUserStateRestored's
      // doc comment (query/UserProvider.tsx) for the data-loss race this prevents (an optimistic
      // write racing ahead of the async restore-and-hydrate cycle silently and permanently
      // discards everything restore would have brought back).
      await waitForUserStateRestored();
      await queryClient.cancelQueries({ queryKey: userStateQueryKey });

      const previousMap = queryClient.getQueryData<UserStateMap>(userStateQueryKey);
      const previousEntry = previousMap?.[itemKey];
      const optimisticEntry = buildOptimisticUserStateEntry(data, version);

      queryClient.setQueryData<UserStateMap>(userStateQueryKey, (current) =>
        applyUserStateEntry(current ?? {}, itemKey, optimisticEntry)
      );

      return { previousMap, previousEntry };
    },
    onError(error, { itemKey }, context) {
      const { entry } = resolveUpdateFailureEntry(error, itemKey, context?.previousEntry);
      queryClient.setQueryData<UserStateMap>(userStateQueryKey, (current) =>
        applyUserStateEntry(current ?? context?.previousMap ?? {}, itemKey, entry)
      );
    },
    onSuccess(serverEntry, { itemKey }) {
      queryClient.setQueryData<UserStateMap>(userStateQueryKey, (current) =>
        applyUserStateEntry(current ?? {}, itemKey, serverEntry)
      );
    },
    // Cache is settled (success, rollback, or 409-reconciled) by the time this fires — re-mirror
    // savedJobs/appliedJobs/viewedJobs into cookies so anonymous SSR (isSaved, match score) sees
    // the change on the next request. See utils/shared/client-preferences.ts.
    onSettled() {
      syncPreferencesToCookies();
    },
  });
}
